import _mapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/map";
import { computed, defineComponent, onMounted, ref, watch } from "@vue/composition-api";
import ReportTabLayout from "../report-tab-layout.vue";
import * as ReportsActions from "../../store/reports-actions";
import { useReportsStoreActions } from "../../store/reports-helpers";
import ReportHistoryLoader from "./report-history-loader.vue";
import SimpleButton from "@Shared/components/buttons/simple-button.vue";
import { useI18n } from "vue-i18n-composable";
import { openFileDialog } from "@Shared/@Utils/utils";
import DocumentPreviewModal from "@Shared/components/document-previewer/document-preview-modal.vue";
import { REPORT_TYPE_E } from "@Broker/@Types/reports/types";
import { useAccountStoreGetters } from "@Broker/@Modules/main/store/account/account-helpers";
import * as AccountGetters from "@Broker/@Modules/main/store/account/account-getters";
import ProfileAvatar from "@Shared/components/general/profile-avatar.vue";
import ReportHistoryTableRowLoader from "./report-history-table-row-loader.vue";
import { useAgentStoreActions, useAgentStoreGetters } from "@Broker/@Modules/agents/store/agent-helpers";
import * as AgentActions from "@Broker/@Modules/agents/store/agent-actions";
import * as AgentGetters from "@Broker/@Modules/agents/store/agent-getters";
import InlineLoader from "@Shared/components/general/inline-loader.vue";
import GenericFilterDropdownMenu from "@Shared/components/general/generic-filter-dropdown-menu.vue";
import { useReportsStoreGetters } from "@Broker/@Modules/clients/reports/store/reports-helpers";
import * as ReportsGetters from "@Broker/@Modules/clients/reports/store/reports-getters";
const TABLE_LIMIT = 20;
const NEVER_FETCHED = -1;
const MAX_COUNT = 9999999;
export default defineComponent({
  name: "ReportHistoryContainer",
  components: {
    ReportTabLayout,
    ReportHistoryLoader,
    SimpleButton,
    DocumentPreviewModal,
    ProfileAvatar,
    ReportHistoryTableRowLoader,
    InlineLoader,
    GenericFilterDropdownMenu
  },
  setup() {
    const {
      t,
      tc
    } = useI18n();
    const {
      LOAD_REPORTS_HISTORY,
      DOWNLOAD_REPORT_BY_ID
    } = useReportsStoreActions([ReportsActions.LOAD_REPORTS_HISTORY, ReportsActions.DOWNLOAD_REPORT_BY_ID]);
    const {
      CURRENT_ACCOUNT_IS_BROKER
    } = useAccountStoreGetters([AccountGetters.CURRENT_ACCOUNT_IS_BROKER]);
    const {
      GET_REPORTS_HISTORY_LOADING_STATE
    } = useReportsStoreGetters([ReportsGetters.GET_REPORTS_HISTORY_LOADING_STATE]);
    const {
      LOAD_AGENTS
    } = useAgentStoreActions([AgentActions.LOAD_AGENTS]);
    const {
      agents
    } = useAgentStoreGetters([AgentGetters.AGENTS]);
    const loadingAgents = ref(false);
    const loadAgentsIfNotLoaded = async () => {
      if (showAgentsActions.value || agents.value.length) return;
      loadingAgents.value = true;
      await LOAD_AGENTS();
      loadingAgents.value = false;
    };
    onMounted(() => {
      loadAgentsIfNotLoaded();
    });
    const previewDocumentModal = ref();
    const loading = ref(false);
    const downloading = ref("");
    const selectedReport = ref(null);
    const selectedReportId = computed(() => {
      var _selectedReport$value;
      return (_selectedReport$value = selectedReport.value) == null ? void 0 : _selectedReport$value.id;
    });
    const selectedReportFileUrl = ref("");
    const showAgentsActions = computed(() => {
      return CURRENT_ACCOUNT_IS_BROKER.value;
    });
    const agentOptions = computed(() => {
      var _context;
      if (!showAgentsActions.value) return [];
      return _mapInstanceProperty(_context = agents.value).call(_context, agent => {
        var _agent$username, _agent$username2;
        return {
          key: (_agent$username = agent.username) != null ? _agent$username : "",
          value: (_agent$username2 = agent.username) != null ? _agent$username2 : "",
          name: `${agent.givenName} ${agent.familyName}`
        };
      });
    });
    const reportTypesOptions = computed(() => [{
      key: REPORT_TYPE_E.APPLICATIONS_SUMMARY,
      value: REPORT_TYPE_E.APPLICATIONS_SUMMARY,
      name: t("application-reports.applications-summary.name").toString()
    }, {
      key: REPORT_TYPE_E.FIRM_STATISTICS,
      value: REPORT_TYPE_E.FIRM_STATISTICS,
      name: t("application-reports.firm-statistics.name").toString()
    }, {
      key: REPORT_TYPE_E.RENEWAL_TRACKING,
      value: REPORT_TYPE_E.RENEWAL_TRACKING,
      name: t("application-reports.renewal-tracking.name").toString()
    }, {
      key: REPORT_TYPE_E.LENDER_TRANSACTIONS,
      value: REPORT_TYPE_E.LENDER_TRANSACTIONS,
      name: t("application-reports.lender-transactions.name").toString()
    }]);
    const tableRef = ref(null);
    const agentFilterState = ref([""]);
    const reportTypeFilterState = ref([""]);
    const allAgentsSelected = computed(() => agentFilterState.value.length === 1 && agentFilterState.value[0] === "");
    const allTypesSelected = computed(() => reportTypeFilterState.value.length === 1 && reportTypeFilterState.value[0] === "");
    const lastTableFetchLength = ref(NEVER_FETCHED);
    const fetchingTableData = ref(false);
    const refreshTableData = () => {
      var _tableRef$value;
      lastTableFetchLength.value = NEVER_FETCHED;
      (_tableRef$value = tableRef.value) == null ? void 0 : _tableRef$value.refresh();
    };
    const tableRequestFunction = async tableOptions => {
      const tableData = {
        data: [],
        count: MAX_COUNT
      };
      const {
        page,
        limit,
        ascending,
        orderBy
      } = tableOptions;
      const offset = (page - 1) * limit;
      const data = await LOAD_REPORTS_HISTORY({
        offset,
        limit,
        orderBy,
        orderType: ascending ? "ASC" : "DESC",
        agentUsernames: allAgentsSelected.value ? undefined : agentFilterState.value,
        reportTypes: allTypesSelected.value ? undefined : reportTypeFilterState.value
      });
      lastTableFetchLength.value = data.length;
      tableData.data = data;
      if (data.length < limit) {
        tableData.count = data.length;
      }
      return tableData;
    };
    const tableOptions = computed(() => {
      const columns = ["type", "format", "requestedAt", "actions"];
      if (showAgentsActions.value) columns.unshift("fullName");
      return {
        columns,
        options: {
          filterable: false,
          sortable: ["requestedAt"],
          sortIcon: {
            base: "fa",
            up: "fa-sort-up",
            down: "fa-sort-down",
            is: "fa-sort"
          },
          orderBy: {
            column: "requestedAt",
            ascending: false
          },
          headings: {
            fullName: t("tables.columns.fullName"),
            type: tc("global.words.type", 1),
            format: tc("application-reports.format"),
            requestedAt: t("application-reports.history.requestedDate"),
            actions: t("global.words.actions")
          },
          skin: "table mb-0",
          perPage: TABLE_LIMIT,
          texts: {
            noResults: t("application-reports.history.noResults")
          },
          columnsClasses: {
            actions: "hidden-heading"
          },
          requestFunction: tableRequestFunction,
          stickyHeader: true,
          pagination: {
            virtual: true
          }
        }
      };
    });
    watch([agentFilterState, reportTypeFilterState], () => {
      refreshTableData();
    });
    const selectedReportName = computed(() => {
      var _selectedReport$value2;
      return (_selectedReport$value2 = selectedReport.value) != null && _selectedReport$value2.type ? t(`application-reports.${selectedReport.value.type}.name`) : "";
    });
    const downloadReportURL = async reportId => {
      const signedUrl = await DOWNLOAD_REPORT_BY_ID({
        reportId
      });
      if (signedUrl) {
        const {
          url
        } = signedUrl;
        return url;
      }
    };
    const handleDownloadReport = async ({
      id,
      type
    }) => {
      downloading.value = id;
      const url = await downloadReportURL(id);
      if (url) {
        openFileDialog(url, t(`application-reports.${type}.name`).toString());
      }
      downloading.value = "";
    };
    const loadingPreviewState = ref({
      loading: false,
      loadingSucceeded: true
    });
    const handleViewReport = async report => {
      loadingPreviewState.value.loading = true;
      selectedReport.value = report;
      const url = await downloadReportURL(report.id);
      if (url) {
        loadingPreviewState.value.loadingSucceeded = true;
        selectedReportFileUrl.value = url;
        previewDocumentModal.value.$showModal();
      } else {
        loadingPreviewState.value.loadingSucceeded = false;
      }
      loadingPreviewState.value.loading = false;
    };
    const handleClosePreview = () => {
      selectedReport.value = null;
      selectedReportFileUrl.value = "";
    };
    const getRequesterAccountDetails = reportItem => {
      const {
        username,
        requesterGivenName: givenName,
        requesterFamilyName: familyName
      } = reportItem;
      return {
        username,
        givenName,
        familyName
      };
    };
    return {
      tableOptions,
      previewDocumentModal,
      selectedReportName,
      loading,
      downloading,
      selectedReport,
      selectedReportId,
      reportTypesOptions,
      handleDownloadReport,
      handleViewReport,
      handleClosePreview,
      getRequesterAccountDetails,
      selectedReportFileUrl,
      loadingPreviewState,
      fetchingTableData,
      loadingAgents,
      agents,
      agentFilterState,
      reportTypeFilterState,
      agentOptions,
      tableRef,
      GET_REPORTS_HISTORY_LOADING_STATE
    };
  }
});